import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Label as RechartsLabel,
} from "recharts";

import Button from "@components/Button";
import { Column, Row } from "@components/Flex";
import { useStores } from "@stores";
import { ROUTES } from "@stores/AccountStore";
import { useLang } from "@src/hooks/useLang";
import { useWeight } from "@src/hooks/useLang";
import { convertWeight } from "@src/utils/convert";
import { WeightUnit } from "@services/demosService";
import {
  userDevices,
  deviceStatistics,
  deviceTrackDates,
  AttachedDevice,
} from "@services/devicesService";
import {
  getLatestCalibration,
} from "@services/calibrationService";
import {
  cameraChartValues,
  normalizeSensor,
  sensorColor,
  sizeBucketLabel,
  SENSOR_PALLET,
  FUEL_PALLET,
} from "@src/screens/TracksScreen/utils";
import DatePicker from "@components/DatePicker/DatePicker";
import {
  fetchWeightRecords,
  createWeightRecord,
  updateWeightRecord,
  deleteWeightRecord,
  calculateWeightStream,
  weightRecordsCsvUrl,
  recordedByLabel,
  WeightRecord,
  CameraTotals,
} from "@services/weightRecordsService";
import {
  fetchWeightChartMarkers,
  createWeightChartMarker,
  updateWeightChartMarkerLabel,
  deleteWeightChartMarker,
  WeightChartMarker,
} from "@services/weightChartMarkersService";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import { downsampleRows } from "@src/utils/chartDownsample";
import {
  TRACKS_RESPONSE_LIMIT,
  isTracksResponseTruncated,
} from "@src/screens/TracksScreen/trackLoad";
import { isJohnDeereCoeffSource, jdWindowAfterImport } from "./johnDeereSource";

// ────────────────────────────────────────────────────────────
// Styles
// ────────────────────────────────────────────────────────────

const SectionCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.2);
  border-radius: 12px;
  padding: 28px;
  width: 100%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 20px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 18px;
  color: #494c5b;
  margin-bottom: 8px;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid rgba(137, 144, 147, 0.3);
  background: #fff;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: #899093;
  transition: all 0.15s;
  flex-shrink: 0;
  &:hover {
    border-color: #68ad18;
    color: #68ad18;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Label = styled.label`
  font-family: Roboto, sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #899093;
  margin-bottom: 6px;
`;

const StyledSelect = styled.select`
  height: 40px;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  padding: 0 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  background: #ffffff;
  cursor: pointer;
  min-width: 200px;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #68ad18;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
  }
`;

const StyledInput = styled.input`
  height: 40px;
  border: 1px solid rgba(137, 144, 147, 0.3);
  border-radius: 6px;
  padding: 0 12px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s;
  &:focus {
    border-color: #68ad18;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 16px;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  user-select: none;
  margin-bottom: 16px;
  /* Recharts wraps SVG in overflow:hidden; marker labels sit in the SVG and must not be clipped */
  .recharts-wrapper {
    overflow: visible !important;
  }
  .recharts-surface {
    overflow: visible;
  }
`;

const ResultBox = styled.div`
  padding: 12px 16px;
  background: #f5f5f5;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const ResultValue = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 20px;
  color: #494c5b;
`;

// The camera columns make the loads table wider than a phone.
const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  margin-top: 16px;
  /* A load followed by its size histogram: one row, one bottom border. */
  tr.with-hist > td {
    border-bottom: none;
    padding-bottom: 4px;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid #e0e0e0;
  color: #899093;
  font-weight: 500;
  font-size: 12px;
`;

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #494c5b;
`;

// Size histogram under a saved load: one tile per bucket, the driver app's
// layout (share on top, bar below) with all 8 camera buckets.
const HistRow = styled.div`
  display: grid;
  grid-template-columns: repeat(8, minmax(52px, 1fr));
  gap: 6px;
  max-width: 720px;
`;

const HistTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 6px 8px 5px;
  background: #f5f5f5;
  border-radius: 8px;
`;

const HistPct = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #494c5b;
`;

const HistBarBox = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  align-items: flex-end;
`;

const HistBar = styled.div`
  width: 100%;
  border-radius: 4px;
  background: #68ad18;
`;

const HistBucket = styled.span`
  font-size: 10px;
  color: #899093;
  white-space: nowrap;
`;

// The same tiles as a preview on one line: in a desktop load row and in the
// calculation result. The tile tint reads on both white and the grey result box.
const MiniHistRow = styled.div`
  display: inline-grid;
  grid-template-columns: repeat(8, 28px);
  gap: 3px;
  vertical-align: middle;
`;

const MiniHistTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 2px 3px 3px;
  background: rgba(137, 144, 147, 0.12);
  border-radius: 5px;
`;

const MiniHistPct = styled.span`
  font-family: Nunito Sans, sans-serif;
  font-weight: 600;
  font-size: 9px;
  line-height: 11px;
  color: #494c5b;
`;

const MiniHistBarBox = styled.div`
  width: 100%;
  height: 14px;
  display: flex;
  align-items: flex-end;
`;

// An average size with its histogram preview beside it.
const SizeWithHist = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
`;

const CommentInput = styled.input`
  border: 1px solid transparent;
  background: transparent;
  padding: 4px 6px;
  font-size: 13px;
  font-family: Roboto, sans-serif;
  color: #494c5b;
  width: 100%;
  border-radius: 4px;
  &:hover {
    border-color: rgba(137, 144, 147, 0.3);
  }
  &:focus {
    outline: none;
    border-color: #68ad18;
    background: #fff;
  }
`;

const SourceBadge = styled.span`
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #eef6e3;
  color: #68ad18;
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #e53935;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover {
    background: #fbe9e7;
  }
`;

const ErrorText = styled.div`
  color: #e53935;
  font-size: 12px;
  margin-top: 4px;
`;

// A caveat about the data on screen (truncated chart, John Deere import lag).
const NoticeText = styled.div`
  padding: 8px 12px;
  margin-bottom: 12px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  line-height: 1.4;
  color: #6d4c00;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px;
  color: #899093;
  font-family: Roboto, sans-serif;
  font-size: 14px;
`;

const CalibrationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: #f0f7e6;
  border: 1px solid #c5e1a5;
  border-radius: 6px;
  font-family: Roboto, sans-serif;
  font-size: 13px;
  color: #494c5b;
`;


const CoeffSource = styled.span`
  font-size: 11px;
  color: #899093;
`;

const SubSectionTitle = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-weight: bold;
  font-size: 15px;
  color: #494c5b;
  margin-bottom: 8px;
`;

const MarkerHint = styled.div`
  font-family: Roboto, sans-serif;
  font-size: 13px;
  color: #68ad18;
  line-height: 1.4;
`;

const MarkerPopoverPanel = styled.div`
  position: fixed;
  z-index: 10050;
  background: #ffffff;
  border: 1px solid rgba(137, 144, 147, 0.35);
  border-radius: 8px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  padding: 10px 12px;
  min-width: 160px;
  font-family: Roboto, sans-serif;
`;

const MarkerPopoverActions = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
`;

const MarkerPopoverBtn = styled.button<{ danger?: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid
    ${({ danger }) => (danger ? "rgba(229, 57, 53, 0.45)" : "rgba(104, 173, 24, 0.45)")};
  background: #fff;
  font-family: Nunito Sans, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ danger }) => (danger ? "#e53935" : "#68ad18")};
  cursor: pointer;
  text-align: center;
  flex: 1;
  min-width: 72px;
  transition: background 0.12s;
  &:hover {
    background: ${({ danger }) => (danger ? "#fbe9e7" : "#f0f7e6")};
  }
`;

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

const MAX_RANGE_DAYS = 10;
const MAX_RANGE_MS = MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
const MAX_GAP_SECONDS = 5 * 60; // 5-minute gap → insert null break (same as Tracks)

function toLocalDatetimeString(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Full current day so far: [today 00:00 local, now] as datetime-local strings (same window as Tracks). */
function currentDayRange(): { start: string; end: string } {
  const now = new Date();
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  return { start: toLocalDatetimeString(dayStart), end: toLocalDatetimeString(now) };
}

/** Linear interpolation — same as Tracks Window.tsx remap() */
function remap(value: number, inStart: number, inEnd: number, outStart: number, outEnd: number): number {
  return (value - inStart) / (inEnd - inStart) * (outEnd - outStart) + outStart;
}

/** Stagger labels when several point markers fall within `gapSec` of each other (same chart column). */
function pointMarkerStackById(
  pointMarkers: WeightChartMarker[],
  gapSec = 120
): Map<string, number> {
  const sorted = [...pointMarkers].sort(
    (a, b) => +new Date(a.start) - +new Date(b.start)
  );
  const map = new Map<string, number>();
  let prevT = -Infinity;
  let stack = -1;
  for (const m of sorted) {
    const t = Math.floor(new Date(m.start).getTime() / 1000);
    if (t - prevT > gapSec) stack = 0;
    else stack += 1;
    map.set(m.id, Math.min(stack, 8));
    prevT = t;
  }
  return map;
}

function clampPopoverPosition(left: number, top: number, panelW = 220, panelH = 140) {
  const margin = 8;
  const maxL = Math.max(margin, window.innerWidth - panelW - margin);
  const maxT = Math.max(margin, window.innerHeight - panelH - margin);
  return {
    left: Math.min(Math.max(margin, left), maxL),
    top: Math.min(Math.max(margin, top), maxT),
  };
}

type ChartPoint = {
  time: number; // unix seconds
  sensor: number | null; // norm * 100 (0–100 scale, same as Tracks), null = gap
  // Potato-camera values as the Tracks panel plots them (cameraChartValues);
  // null = no camera reading, or a gap.
  potato: number | null;
  stone: number | null;
  size: number | null;
  // Fuel sensor input, mV (null = no reading, or a gap).
  fuel: number | null;
};

const GAP_POINT = { sensor: null, potato: null, stone: null, size: null, fuel: null };

type CameraKey = "potato" | "stone" | "size";

// The charts under the sensor chart for a potato-camera machine: the Tracks
// panel's camera metrics, labels and ramps (LAYER_METRICS in
// TracksScreen/DeviceWindow/Window.tsx). Lots of not-potatoes is bad, so that
// ramp runs green→red.
const CAMERA_METRICS: Array<{
  dataKey: CameraKey;
  langKey: "potatoLayerTitle" | "notPotatoLayerTitle" | "sizeLayerTitle";
  fallbackLabel: string;
  unit?: string;
  decimals: number;
  reversed: boolean;
}> = [
  { dataKey: "potato", langKey: "potatoLayerTitle", fallbackLabel: "Potatoes", decimals: 0, reversed: false },
  { dataKey: "stone", langKey: "notPotatoLayerTitle", fallbackLabel: "Not potatoes", decimals: 0, reversed: true },
  { dataKey: "size", langKey: "sizeLayerTitle", fallbackLabel: "Average size", unit: "px", decimals: 1, reversed: false },
];

const CAMERA_CHART_HEIGHT = 160;

/** A span on the chart's time axis, in unix seconds. */
type TimeSpan = { x1: number; x2: number };

/**
 * The selection snapped to the first and last drawn point inside it: a
 * ReferenceArea that reaches past the data is dropped by Recharts.
 */
function selectionSpan(rows: ChartPoint[], selStart: string, selEnd: string): TimeSpan | null {
  if (!selStart || !selEnd) return null;
  const s = Math.floor(new Date(selStart).getTime() / 1000);
  const e = Math.floor(new Date(selEnd).getTime() / 1000);
  let x1: number | null = null;
  let x2: number | null = null;
  for (const r of rows) {
    if (x1 == null && r.time >= s) x1 = r.time;
    if (r.time <= e) x2 = r.time;
  }
  return x1 != null && x2 != null && x1 <= x2 ? { x1, x2 } : null;
}

type ChartMouseHandlers = {
  onMouseDown: (e: any) => void;
  onMouseMove: (e: any) => void;
  onMouseUp: () => void;
};

type ChartLabelViewBox = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
};

type MarkerPillAnchor = { top: number; left: number };

function truncateMarkerLabel(text: string, maxChars = 22): string {
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(1, maxChars - 1))}…`;
}

/**
 * Native SVG label (rect + text). `foreignObject` + HTML was clipped / invisible in Recharts’ SVG.
 */
function ChartMarkerNamePill(props: {
  viewBox?: ChartLabelViewBox;
  marker: WeightChartMarker;
  placement: "point" | "interval";
  onPillClick: (markerId: string, anchor: MarkerPillAnchor) => void;
  /** Vertical stagger when multiple point markers are close in time */
  pointStackIndex?: number;
}) {
  const { viewBox, marker, placement, onPillClick, pointStackIndex = 0 } = props;
  const vx = viewBox?.x ?? 0;
  const vy = viewBox?.y ?? 0;
  const vw = viewBox?.width ?? 0;

  const display = truncateMarkerLabel(marker.label);
  const pillW = Math.min(140, Math.max(36, display.length * 6.5));

  let cx: number;
  let textY: number;
  if (placement === "point") {
    cx = vx;
    /* "top" anchor is upper end of the line — place pill just inside the plot, stack downward */
    textY = vy + 14 + pointStackIndex * 16;
  } else {
    cx = vx + vw / 2;
    textY = vy + 14;
  }

  const handlePillClick = (e: React.MouseEvent<SVGGElement>) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    onPillClick(marker.id, {
      top: r.bottom + 4,
      left: r.left,
    });
  };

  return (
    <g
      data-chart-marker-pill
      onMouseDown={(e) => e.stopPropagation()}
      onClick={handlePillClick}
      style={{ cursor: "pointer" }}
    >
      <title>{marker.label}</title>
      <rect
        x={cx - pillW / 2}
        y={textY - 11}
        width={pillW}
        height={20}
        rx={6}
        fill="rgba(255, 255, 255, 0.96)"
        stroke="rgba(137, 144, 147, 0.45)"
        strokeWidth={1}
      />
      <text
        x={cx}
        y={textY}
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily="Nunito Sans, sans-serif"
        fontSize={11}
        fontWeight={600}
        fill="#494c5b"
        style={{ pointerEvents: "none" }}
      >
        {display}
      </text>
    </g>
  );
}

function CalcProgress(props: {
  done: number;
  total: number;
  runningKg: number;
  toDisplayUnit: (kg: number) => number;
  unitLabel: string;
  labelTemplate?: string; // e.g. "Day {done} of {total}"
  runningLabel: string;
}) {
  const { done, total, runningKg, toDisplayUnit, unitLabel, labelTemplate, runningLabel } = props;
  const pct = Math.min(100, Math.round((done / total) * 100));
  const tpl = labelTemplate ?? "Day {done} of {total}";
  const label = tpl.replace("{done}", String(done)).replace("{total}", String(total));
  return (
    <div style={{ marginTop: 8, fontSize: 13, color: "#555" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span>{label}</span>
        <span>
          {runningLabel}: {toDisplayUnit(runningKg).toFixed(2)} {unitLabel}
        </span>
      </div>
      <div
        style={{
          height: 6,
          background: "#eef2e6",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            background: "#68ad18",
            transition: "width 120ms ease-out",
          }}
        />
      </div>
    </div>
  );
}

/**
 * One potato-camera metric under the sensor chart, drawn like the Tracks
 * panel's camera chart: absolute units, the red→green ramp from the axis floor
 * to the window's peak. Dragging on it selects the window, as on the sensor chart.
 */
function CameraChart(props: {
  rows: ChartPoint[];
  dataKey: CameraKey | "fuel";
  label: string;
  unit?: string;
  decimals: number;
  reversed: boolean;
  /** Own ramp instead of red→green (fuel: the Tracks map's amber ramp). */
  pallet?: string[];
  /** Axis this far around the data instead of from 0 (fuel mV sits far from 0). */
  pad?: number;
  /** The drag in progress. */
  drag: TimeSpan | null;
  selection: TimeSpan | null;
  mouse: ChartMouseHandlers;
}) {
  const { rows, dataKey, label, unit, decimals, reversed, pad, drag, selection, mouse } = props;
  const pallet = props.pallet ?? (reversed ? [...SENSOR_PALLET].reverse() : SENSOR_PALLET);
  const gradientId = `wt_camera_fill_${dataKey}`;
  const span = drag ?? selection;
  return (
    <ChartContainer>
      <Label>{unit ? `${label} (${unit})` : label}</Label>
      <ResponsiveContainer width="100%" height={CAMERA_CHART_HEIGHT}>
        <AreaChart data={rows} margin={{ top: 8, right: 10, left: 6, bottom: 6 }} {...mouse}>
          <defs>
            {/* Top of the chart = highest value, so the ramp is emitted in
                reverse to match the SVG's top-down gradient axis. */}
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              {pallet.map((_, i) => (
                <stop
                  key={i}
                  offset={i / (pallet.length - 1)}
                  stopColor={pallet[pallet.length - 1 - i]}
                  stopOpacity={1}
                />
              ))}
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            tickFormatter={(val) => moment.unix(val).format("HH:mm")}
            scale="linear"
            interval="equidistantPreserveStart"
          />
          {/* Counts tick in whole numbers: a window peaking at 1 stone
              otherwise ticks 0, 0.25 … 1, rounded to "0, 0, 1, 1, 1". */}
          <YAxis
            dataKey={dataKey}
            domain={
              pad != null
                ? [(min: number) => Math.max(0, Math.floor(min) - pad), (max: number) => Math.ceil(max) + pad]
                : [0, "auto"]
            }
            allowDecimals={decimals > 0}
            tickFormatter={(val) => Number(val).toFixed(decimals)}
          />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={`url(#${gradientId})`}
            fill={`url(#${gradientId})`}
            fillOpacity={1}
            connectNulls={false}
            isAnimationActive={false}
            activeDot={{ r: 3 }}
          />
          <Tooltip
            labelFormatter={(l) => moment.unix(l).format("HH:mm:ss")}
            formatter={(value: number) => {
              if (value == null) return ["-", label];
              const text = value.toFixed(decimals);
              return [unit ? `${text} ${unit}` : text, label];
            }}
          />
          {span && (
            <ReferenceArea
              x1={span.x1}
              x2={span.x2}
              strokeOpacity={0.3}
              fill="#68ad18"
              fillOpacity={0.15}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
}

/** "1,234" for a count, "45.5 px" for a size; "—" when there is none. */
function formatCount(n: number | null | undefined): string {
  return n == null ? "—" : Math.round(n).toLocaleString();
}

function formatSizePx(px: number | null | undefined): string {
  return px == null ? "—" : `${px.toFixed(1)} px`;
}

/** True when a histogram sized at least one potato. */
function hasSizes(hist: number[] | null | undefined): hist is number[] {
  return Array.isArray(hist) && hist.some((n) => n > 0);
}

/**
 * A load's size distribution: per bucket, its share of the sized potatoes and
 * a bar scaled to the fullest bucket. `compact` is the one-line preview (no
 * bucket labels; hovering a tile names its range). Null when nothing was sized.
 */
function SizeHistogram({ hist, compact = false }: { hist: number[]; compact?: boolean }) {
  const total = hist.reduce((sum, n) => sum + (n || 0), 0);
  if (total <= 0) return null;
  const max = Math.max(...hist);
  const Row = compact ? MiniHistRow : HistRow;
  const Tile = compact ? MiniHistTile : HistTile;
  const Pct = compact ? MiniHistPct : HistPct;
  const BarBox = compact ? MiniHistBarBox : HistBarBox;
  const barMax = compact ? 14 : 32;
  return (
    <Row>
      {hist.map((count, k) => {
        const pct = Math.round((count / total) * 100);
        return (
          <Tile key={k} title={`${sizeBucketLabel(k)}: ${count.toLocaleString()} (${pct}%)`}>
            <Pct>{pct}%</Pct>
            <BarBox>
              {/* An empty bucket keeps a 2 px line, as in the driver app. */}
              <HistBar style={{ height: Math.max(2, Math.round((barMax * count) / max)) }} />
            </BarBox>
            {!compact && <HistBucket>{sizeBucketLabel(k)}</HistBucket>}
          </Tile>
        );
      })}
    </Row>
  );
}

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

const WeightTracking: React.FC = observer(() => {
  const { settingsStore, accountStore } = useStores();
  const navigate = useNavigate();
  const lang = useLang();
  const weightLabel = useWeight();
  const wt = lang.analytics;

  const units = settingsStore.units;
  const unitLabel = weightLabel();

  // Device list
  const [devices, setDevices] = useState<AttachedDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [loadingDevices, setLoadingDevices] = useState(true);

  // Time range — defaults to the full current day (00:00 → now), like Tracks
  const [startTime, setStartTime] = useState<string>(() => currentDayRange().start);
  const [endTime, setEndTime] = useState<string>(() => currentDayRange().end);
  const [rangeError, setRangeError] = useState<string>("");

  // Calendar state for dates with data
  const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => new Date());
  const [datesWithData, setDatesWithData] = useState<Date[]>([]);

  // Chart data
  const [chartData, setChartData] = useState<ChartPoint[]>([]);
  const [loadingChart, setLoadingChart] = useState(false);
  /** The tracks response stopped at the row cap: the chart ends early. */
  const [chartTruncated, setChartTruncated] = useState(false);
  /** The window carries potato-camera readings. */
  const [chartHasCamera, setChartHasCamera] = useState(false);
  /** The device has a fuel sensor (admin switch) and the window carries readings. */
  const [chartHasFuel, setChartHasFuel] = useState(false);
  // What the chart draws: a 1 Hz John Deere day is tens of thousands of
  // points. Selection and markers work in this series' indices; the weight is
  // computed by the backend from every point, so it is unaffected.
  const drawnData = useMemo(() => downsampleRows(chartData, "sensor").rows, [chartData]);
  /** Increment to refetch chart + weight records without changing device or time range. */
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  // Selection on chart. The drag is held in unix seconds, not row indices: the
  // sensor and camera charts are each downsampled on their own values, so one
  // index is a different time on each.
  const [refAreaLeft, setRefAreaLeft] = useState<number | undefined>();
  const [refAreaRight, setRefAreaRight] = useState<number | undefined>();
  const [selStart, setSelStart] = useState<string>("");
  const [selEnd, setSelEnd] = useState<string>("");

  // Calibration state. The coefficient itself is never sent: the backend picks
  // the calibration that was in force for each part of the window (falling back
  // to the device-type default) and reports which one it used. A John Deere
  // combine without a GreenGrowth calibration weighs Deere's own mass:
  // "johndeere", never "estimated".
  const [calCoeffSource, setCalCoeffSource] = useState<
    "calibration" | "fallback" | "johndeere" | "loading"
  >("loading");
  const [hasCalibrations, setHasCalibrations] = useState(false);

  // Calculation result. The camera totals come with the weight (same
  // calculation) and are shown and saved only next to it.
  const [calculatedWeightKg, setCalculatedWeightKg] = useState<number | null>(null);
  const [calculatedCamera, setCalculatedCamera] = useState<CameraTotals | null>(null);

  // Saved records
  const [records, setRecords] = useState<WeightRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);

  // Named plot markers (point or interval)
  const [markers, setMarkers] = useState<WeightChartMarker[]>([]);
  const [placementMode, setPlacementMode] = useState<"none" | "point">("none");
  const [markerPending, setMarkerPending] = useState<
    | null
    | { kind: "point"; timeSec: number }
    | { kind: "interval"; start: string; end: string }
  >(null);
  const [markerDraftName, setMarkerDraftName] = useState("");
  const [savingMarker, setSavingMarker] = useState(false);
  const [markerMenu, setMarkerMenu] = useState<null | {
    markerId: string;
    mode: "actions" | "edit";
    anchor: MarkerPillAnchor;
  }>(null);
  const [markerEditName, setMarkerEditName] = useState("");

  // Get ESN from selected device
  const selectedDevice = useMemo(
    () => devices.find((d) => d.getId() === selectedDeviceId),
    [devices, selectedDeviceId]
  );
  const deviceEsn = selectedDevice?.getEsn() ?? "";
  const selectedIsJohnDeere = selectedDevice?.getSource() === "johndeere";
  // When the combine's harvest was last imported from Deere (device list);
  // null for our own hardware.
  const jdImport = selectedIsJohnDeere ? selectedDevice?.getJohnDeere() ?? null : null;
  // Strings added for John Deere combines and the chart's row cap; a locale
  // that does not carry them yet falls back to the English text. The import
  // status lines are the device list's own (devices.*).
  const wtNew = wt as Record<string, string | undefined>;
  const devicesLang = lang.devices as Record<string, string | undefined>;
  const jdLabel = wtNew.coeffSourceJohnDeere ?? "John Deere";

  // ── Load devices ────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadingDevices(true);
    userDevices()
      .then(({ devices: devs }) => {
        if (cancelled) return;
        setDevices(devs);
        if (devs.length > 0 && !selectedDeviceId) {
          setSelectedDeviceId(devs[0].getId());
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingDevices(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load calibration for selected device ────────────────
  useEffect(() => {
    if (!selectedDeviceId) {
      setCalCoeffSource("loading");
      setHasCalibrations(false);
      return;
    }
    let cancelled = false;
    setCalCoeffSource("loading");

    const uncalibrated = selectedIsJohnDeere ? "johndeere" : "fallback";
    const loadLatestCal = () => {
      getLatestCalibration(selectedDeviceId)
        .then((latest) => {
          if (cancelled) return;
          setCalCoeffSource(latest ? "calibration" : uncalibrated);
          setHasCalibrations(Boolean(latest));
        })
        .catch(() => {
          if (cancelled) return;
          setCalCoeffSource(uncalibrated);
          setHasCalibrations(false);
        });
    };

    loadLatestCal();

    const onVisibility = () => {
      if (document.visibilityState === "visible" && !cancelled) {
        loadLatestCal();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [selectedDeviceId, selectedIsJohnDeere]);

  // ── Fetch dates with track data for calendar dots ──────
  useEffect(() => {
    if (!selectedDeviceId) {
      setDatesWithData([]);
      return;
    }
    const from = moment(visibleMonth).startOf("month").toDate();
    const to = moment(visibleMonth).endOf("month").toDate();
    let cancelled = false;
    deviceTrackDates(selectedDeviceId, from, to)
      .then((dates) => { if (!cancelled) setDatesWithData(dates); })
      .catch(() => { if (!cancelled) setDatesWithData([]); });
    return () => { cancelled = true; };
  }, [selectedDeviceId, visibleMonth]);

  // ── Validate time range ─────────────────────────────────
  useEffect(() => {
    const s = new Date(startTime);
    const e = new Date(endTime);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) {
      setRangeError("");
      return;
    }
    if (e.getTime() - s.getTime() > MAX_RANGE_MS) {
      setRangeError(
        wt.maxRangeError ?? `Time range cannot exceed ${MAX_RANGE_DAYS} days`
      );
    } else if (s >= e) {
      setRangeError("Start must be before end");
    } else {
      setRangeError("");
    }
  }, [startTime, endTime, wt]);

  /** Refresh: viewing today → extend the window to the full current day (00:00 → now);
   *  viewing a past day → keep the range and just refetch. */
  const handleWeightTrackingRefresh = useCallback(() => {
    const now = new Date();
    if (selectedDate.toDateString() === now.toDateString()) {
      const { start, end } = currentDayRange();
      setStartTime(start);
      setEndTime(end);
      setSelectedDate(now);
    }
    setDataRefreshKey((k) => k + 1);
  }, [selectedDate]);

  // ── Load chart data when device or time range changes ───
  // Chart plots norm values (0–100) from the backend, independent of the
  // calibration coefficient, which only the backend calculate endpoint applies.
  useEffect(() => {
    if (!selectedDeviceId || rangeError) {
      setChartData([]);
      setChartTruncated(false);
      setChartHasCamera(false);
      setChartHasFuel(false);
      return;
    }
    const s = new Date(startTime);
    const e = new Date(endTime);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || s >= e) return;

    let cancelled = false;
    setLoadingChart(true);
    setCalculatedWeightKg(null);
    setSelStart("");
    setSelEnd("");

    deviceStatistics(selectedDeviceId, s, e)
      .then((stats) => {
        if (cancelled) return;
        const timeArr = stats.getTimeList();
        const normArr = stats.getNormList();
        const camera = cameraChartValues(stats);
        const fuels = stats.getFuelMvList();
        const points: ChartPoint[] = [];

        for (let i = 0; i < stats.getLength(); i++) {
          // Skip duplicate timestamps
          if (i > 0 && timeArr[i] === timeArr[i - 1]) continue;
          // Insert null gap if >5 min break between points (same as Tracks)
          if (points.length > 0 && Math.abs(timeArr[i] - points[points.length - 1].time) > MAX_GAP_SECONDS) {
            points.push({ ...GAP_POINT, time: points[points.length - 1].time + 1 });
            points.push({ ...GAP_POINT, time: timeArr[i] - 1 });
          }
          points.push({
            time: timeArr[i],
            sensor: Math.floor(normArr[i] * 100),
            potato: camera.potato[i] ?? null,
            stone: camera.stone[i] ?? null,
            size: camera.size[i] ?? null,
            fuel: fuels[i] ?? null,
          });
        }
        setChartData(points);
        setChartTruncated(isTracksResponseTruncated(stats.getLength()));
        setChartHasCamera(stats.hasCameraData());
        setChartHasFuel(stats.hasFuelData());
      })
      .catch(() => {
        setChartData([]);
        setChartTruncated(false);
        setChartHasCamera(false);
        setChartHasFuel(false);
      })
      .finally(() => { if (!cancelled) setLoadingChart(false); });

    return () => { cancelled = true; };
  }, [selectedDeviceId, startTime, endTime, rangeError, dataRefreshKey]);

  // ── Load saved records when device changes ──────────────
  useEffect(() => {
    if (!deviceEsn) {
      setRecords([]);
      return;
    }
    let cancelled = false;
    setLoadingRecords(true);
    fetchWeightRecords(deviceEsn)
      .then((recs) => { if (!cancelled) setRecords(recs); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingRecords(false); });
    return () => { cancelled = true; };
  }, [deviceEsn, dataRefreshKey]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") setPlacementMode("none");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Plot markers (named annotations) ────────────────────
  useEffect(() => {
    if (!deviceEsn) {
      setMarkers([]);
      return;
    }
    let cancelled = false;
    fetchWeightChartMarkers(deviceEsn)
      .then((m) => {
        if (!cancelled) setMarkers(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [deviceEsn, dataRefreshKey]);

  const visibleMarkers = useMemo(() => {
    const ws = Math.floor(new Date(startTime).getTime() / 1000);
    const we = Math.ceil(new Date(endTime).getTime() / 1000);
    if (!Number.isFinite(ws) || !Number.isFinite(we) || ws > we) return [];
    return markers.filter((m) => {
      const a = Math.floor(new Date(m.start).getTime() / 1000);
      const b = Math.floor(new Date(m.stop).getTime() / 1000);
      return b >= ws && a <= we;
    });
  }, [markers, startTime, endTime]);

  const { pointMarkers, intervalMarkers } = useMemo(() => {
    const points: WeightChartMarker[] = [];
    const intervals: WeightChartMarker[] = [];
    for (const m of visibleMarkers) {
      if (m.kind === "point") points.push(m);
      else intervals.push(m);
    }
    return { pointMarkers: points, intervalMarkers: intervals };
  }, [visibleMarkers]);

  const pointMarkerStacks = useMemo(
    () => pointMarkerStackById(pointMarkers),
    [pointMarkers]
  );

  const handleSaveMarker = useCallback(async () => {
    const name = markerDraftName.trim();
    if (!name || !deviceEsn || !markerPending) return;
    setSavingMarker(true);
    try {
      if (markerPending.kind === "point") {
        const d = new Date(markerPending.timeSec * 1000);
        const iso = d.toISOString();
        const created = await createWeightChartMarker({
          deviceEsn,
          label: name,
          kind: "point",
          start: iso,
          stop: iso,
        });
        setMarkers((prev) => [created, ...prev]);
      } else {
        const s = new Date(markerPending.start);
        const en = new Date(markerPending.end);
        if (isNaN(s.getTime()) || isNaN(en.getTime()) || s >= en) return;
        const created = await createWeightChartMarker({
          deviceEsn,
          label: name,
          kind: "interval",
          start: s.toISOString(),
          stop: en.toISOString(),
        });
        setMarkers((prev) => [created, ...prev]);
      }
      setMarkerPending(null);
      setMarkerDraftName("");
      toast.success(wt.markerSaved ?? "Marker saved");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to save marker");
    } finally {
      setSavingMarker(false);
    }
  }, [markerDraftName, deviceEsn, markerPending, wt]);

  const handleDeleteMarker = useCallback(
    async (id: string): Promise<boolean> => {
      if (!window.confirm(wt.markerDeleteConfirm ?? "Delete this marker?")) return false;
      try {
        await deleteWeightChartMarker(id);
        setMarkers((prev) => prev.filter((m) => m.id !== id));
        return true;
      } catch {
        toast.error(wt.markerDeleteFailed ?? "Failed to delete marker");
        return false;
      }
    },
    [wt]
  );

  const handleUpdateMarkerLabel = useCallback(
    async (id: string, label: string): Promise<boolean> => {
      try {
        const updated = await updateWeightChartMarkerLabel(id, label);
        setMarkers((prev) => prev.map((m) => (m.id === id ? updated : m)));
        return true;
      } catch {
        toast.error(wt.markerUpdateFailed ?? "Failed to update marker");
        return false;
      }
    },
    [wt]
  );

  const closeMarkerMenu = useCallback(() => {
    setMarkerMenu(null);
    setMarkerEditName("");
  }, []);

  const handleMarkerPillClick = useCallback((markerId: string, anchor: MarkerPillAnchor) => {
    const clamped = clampPopoverPosition(anchor.left, anchor.top);
    setMarkerMenu({ markerId, mode: "actions", anchor: clamped });
    setMarkerEditName("");
  }, []);

  const saveMarkerLabelFromPopover = useCallback(async () => {
    if (!markerMenu || markerMenu.mode !== "edit") return;
    const t = markerEditName.trim();
    if (!t) return;
    const ok = await handleUpdateMarkerLabel(markerMenu.markerId, t);
    if (ok) {
      toast.success(wt.markerUpdated ?? "Marker updated");
      closeMarkerMenu();
    }
  }, [markerMenu, markerEditName, handleUpdateMarkerLabel, wt, closeMarkerMenu]);

  useEffect(() => {
    if (!markerMenu) return;
    const onDocDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest("#marker-chart-popover") || el.closest("[data-chart-marker-pill]")) return;
      closeMarkerMenu();
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [markerMenu, closeMarkerMenu]);

  useEffect(() => {
    if (!markerMenu) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMarkerMenu();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [markerMenu, closeMarkerMenu]);

  // ── Chart mouse handlers for drag-to-select ─────────────
  // Drag-to-select is desktop-only: on a phone it competes with scrolling and
  // wins (bug-109). Phones pick the window with the date/time inputs; a tap
  // still places a marker in point mode.
  const isMobile = useIsMobile();
  // The handlers of a chart drawing `rows`: the sensor chart and each camera
  // chart pass their own (separately downsampled) rows.
  const chartMouse = useCallback(
    (rows: ChartPoint[]): ChartMouseHandlers => {
      const timeAt = (e: any): number | undefined =>
        e?.activeTooltipIndex != null ? rows[e.activeTooltipIndex]?.time : undefined;
      return {
        onMouseDown: (e: any) => {
          const t = timeAt(e);
          if (placementMode === "point") {
            if (t != null) {
              setMarkerPending({ kind: "point", timeSec: t });
              setMarkerDraftName("");
              setPlacementMode("none");
            }
            return;
          }
          if (isMobile || t == null) return;
          setRefAreaLeft(t);
          setRefAreaRight(undefined);
        },
        onMouseMove: (e: any) => {
          const t = timeAt(e);
          if (refAreaLeft != null && t != null) setRefAreaRight(t);
        },
        onMouseUp: () => {
          if (refAreaLeft != null && refAreaRight != null) {
            const left = Math.min(refAreaLeft, refAreaRight);
            const right = Math.max(refAreaLeft, refAreaRight);
            setSelStart(toLocalDatetimeString(new Date(left * 1000)));
            setSelEnd(toLocalDatetimeString(new Date(right * 1000)));
            setCalculatedWeightKg(null);
          }
          setRefAreaLeft(undefined);
          setRefAreaRight(undefined);
        },
      };
    },
    [placementMode, isMobile, refAreaLeft, refAreaRight]
  );
  const dragSpan: TimeSpan | null =
    refAreaLeft != null && refAreaRight != null
      ? { x1: Math.min(refAreaLeft, refAreaRight), x2: Math.max(refAreaLeft, refAreaRight) }
      : null;

  // ── Calculate weight for selection (backend, streaming) ───
  const [calculating, setCalculating] = useState(false);
  const [calcProgress, setCalcProgress] = useState<{
    done: number;
    total: number;
    runningKg: number;
  } | null>(null);
  const calcAbortRef = useRef<AbortController | null>(null);

  const handleCalculate = useCallback(async () => {
    const s = new Date(selStart);
    const e = new Date(selEnd);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || s >= e || !deviceEsn) return;

    const controller = new AbortController();
    calcAbortRef.current = controller;
    setCalculating(true);
    setCalcProgress({ done: 0, total: 0, runningKg: 0 });
    try {
      const result = await calculateWeightStream({
        deviceEsn,
        start: s.toISOString(),
        stop: e.toISOString(),
        signal: controller.signal,
        onProgress: (p) => {
          setCalcProgress({
            done: p.chunkIndex + 1,
            total: p.totalChunks,
            runningKg: p.runningWeightKg,
          });
        },
      });
      setCalculatedWeightKg(result.weightKg);
      setCalculatedCamera(result.camera ?? null);
      setCalCoeffSource(
        result.calCoeffSource === "calibration"
          ? "calibration"
          : selectedIsJohnDeere || isJohnDeereCoeffSource(result.calCoeffSource)
            ? "johndeere"
            : "fallback"
      );
    } catch (err: any) {
      if (err?.name === "AbortError") {
        // User-initiated cancel — silent.
      } else {
        toast.error(err?.message ?? "Failed to calculate weight");
      }
    } finally {
      setCalculating(false);
      setCalcProgress(null);
      calcAbortRef.current = null;
    }
  }, [selStart, selEnd, deviceEsn, selectedIsJohnDeere]);

  const handleCancelCalculate = useCallback(() => {
    calcAbortRef.current?.abort();
  }, []);

  // ── Save record ─────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (calculatedWeightKg == null || !deviceEsn) return;
    const s = new Date(selStart);
    const e = new Date(selEnd);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return;

    try {
      const record = await createWeightRecord({
        deviceEsn,
        start: s.toISOString(),
        stop: e.toISOString(),
        weightKg: calculatedWeightKg,
        ...(calculatedCamera && {
          cvPotatoCount: calculatedCamera.potatoCount,
          cvStoneCount: calculatedCamera.stoneCount,
          cvAvgSizePx: calculatedCamera.avgSizePx,
          cvSizeHist: calculatedCamera.sizeHist ?? null,
        }),
      });
      setRecords((prev) => [record, ...prev]);
      setCalculatedWeightKg(null);
      setSelStart("");
      setSelEnd("");
      toast.success(wt.saveSuccess ?? "Record saved");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to save");
    }
  }, [calculatedWeightKg, calculatedCamera, deviceEsn, selStart, selEnd, wt]);

  // ── Update comment ──────────────────────────────────────
  const handleUpdateComment = useCallback(
    async (id: string, comment: string) => {
      try {
        const updated = await updateWeightRecord(id, comment);
        setRecords((prev) =>
          prev.map((r) => (r.id === id ? updated : r))
        );
      } catch {
        toast.error("Failed to update comment");
      }
    },
    []
  );

  // ── Delete record ───────────────────────────────────────
  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm(wt.deleteConfirm ?? "Are you sure you want to delete this record?")) return;
      try {
        await deleteWeightRecord(id);
        setRecords((prev) => prev.filter((r) => r.id !== id));
      } catch {
        toast.error("Failed to delete");
      }
    },
    [wt]
  );

  // ── Convert kg to display unit ──────────────────────────
  const toDisplayUnit = useCallback(
    (kg: number) =>
      convertWeight(kg, WeightUnit.Kilogram, units.weight, {
        poundsPerBushel: units.poundsPerBushel,
        poundsPerBag: units.poundsPerBag,
      }),
    [units]
  );

  // ── Selection for ReferenceArea, on each chart's own drawn points ──
  const selection = useMemo(
    () => selectionSpan(drawnData, selStart, selEnd),
    [selStart, selEnd, drawnData]
  );

  // ── Potato-camera charts ────────────────────────────────
  // As on Tracks: only for an account the admin marked as having the potato
  // camera, and only when this window carries camera readings. A static
  // camera machine has no track on the map, so this is where it is read.
  const showCamera = accountStore.cameraInstalled && chartHasCamera;
  const cameraCharts = useMemo(
    () =>
      showCamera
        ? CAMERA_METRICS.map((metric) => ({
            metric,
            rows: downsampleRows(chartData, metric.dataKey).rows,
          }))
        : [],
    [showCamera, chartData]
  );
  const tracksLang = lang.tracks as Record<string, string | undefined>;
  const cameraLabel = (key: CameraKey) => {
    const metric = CAMERA_METRICS.find((m) => m.dataKey === key)!;
    return tracksLang[metric.langKey] ?? metric.fallbackLabel;
  };
  // Fuel sensor input (mV) — devices an admin marked "fuel sensor connected".
  const fuelRows = useMemo(
    () => (chartHasFuel ? downsampleRows(chartData, "fuel").rows : null),
    [chartHasFuel, chartData]
  );
  // Camera columns in the loads table once a load carries camera totals.
  const recordsHaveCamera = records.some((r) => r.cvPotatoCount != null);

  // Compute chart max for gradient calculation (same approach as Tracks SensorChart)
  const chartMax = useMemo(() => {
    let max = 0;
    for (const p of chartData) {
      if (p.sensor != null && p.sensor > max) max = p.sensor;
    }
    return max;
  }, [chartData]);

  // Number of gradient color stops (mapped from 0–100 sensor range to 0–10 palette entries)
  const gradientLength = Math.round(remap(Math.min(chartMax, 100), 0, 100, 0, 10));

  // The tracks endpoint returns the first TRACKS_RESPONSE_LIMIT rows of the
  // window, so a long window of a 1 Hz John Deere combine ends early on the
  // chart. The weight is summed server-side over every row.
  const truncatedNotice =
    chartTruncated && chartData.length > 0
      ? (
          wtNew.chartTruncated ??
          "The chart shows only the first {limit} points of this window, up to {time}. Pick a shorter window to see the rest. The calculated weight uses every point."
        )
          .replace("{limit}", TRACKS_RESPONSE_LIMIT.toLocaleString())
          .replace("{time}", moment.unix(chartData[chartData.length - 1].time).format("YYYY-MM-DD HH:mm"))
      : null;

  // John Deere data arrives in sync batches every few hours, so harvest at the
  // end of a recent window may not be imported yet — and a weight record saved
  // now would keep the undercount. Compared against the selection when there is
  // one (that is what gets weighed), else the window.
  const jdWindowEnd = new Date(selEnd || endTime);
  const jdLastImport = jdImport?.lastImportAt
    ? moment(jdImport.lastImportAt).format("YYYY-MM-DD HH:mm")
    : null;
  const jdNotice: { text: string; warn: boolean } | null = !jdImport
    ? null
    : !jdLastImport
      ? {
          text: devicesLang.jdNoImportYet ?? "Waiting for the first John Deere import",
          warn: true,
        }
      : !Number.isNaN(jdWindowEnd.getTime()) && jdWindowAfterImport(jdWindowEnd, jdImport)
        ? {
            text: (
              wtNew.jdWindowAfterImport ??
              "This window ends after the last John Deere import ({time}). Deere sends harvest data every few hours, so the weight can still grow."
            ).replace("{time}", jdLastImport),
            warn: true,
          }
        : {
            text: (devicesLang.jdLastImport ?? "Last John Deere import {time}").replace("{time}", jdLastImport),
            warn: false,
          };

  // ── Render ──────────────────────────────────────────────
  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitle style={{ marginBottom: 0 }}>
          {wt.weightTrackingTitle ?? "Weight Tracking"} ({unitLabel})
        </SectionTitle>
        <RefreshButton
          type="button"
          onClick={handleWeightTrackingRefresh}
          disabled={!selectedDeviceId || !!rangeError || loadingChart}
          title={lang.analytics?.refreshButton ?? "Refresh"}
        >
          ↻ {lang.analytics?.refreshButton ?? "Refresh"}
        </RefreshButton>
      </SectionHeader>

      <ControlsRow>
        {/* Device selector */}
        <Column style={{ gap: 4 }}>
          <Label>{wt.selectDevice ?? "Select a device"}</Label>
          <StyledSelect
            value={selectedDeviceId}
            onChange={(e) => {
              setSelectedDeviceId(e.target.value);
              setCalculatedWeightKg(null);
              setSelStart("");
              setSelEnd("");
              setPlacementMode("none");
              setMarkerPending(null);
              setMarkerDraftName("");
              // Reset to the full current day so the new device's data shows immediately
              const { start, end } = currentDayRange();
              setStartTime(start);
              setEndTime(end);
              setSelectedDate(new Date());
            }}
            disabled={loadingDevices}
          >
            {devices.length === 0 && (
              <option value="">{lang.tracks?.noDevices ?? "No devices found"}</option>
            )}
            {devices.map((d) => (
              <option key={d.getId()} value={d.getId()}>
                {d.getName() || d.getEsn()}
              </option>
            ))}
          </StyledSelect>
        </Column>

        {/* Calibration info + Calibrate button */}
        {selectedDeviceId && (
          <Column style={{ gap: 4 }}>
            <Label>{wt.calCoefficient ?? "Calibration"}</Label>
            {calCoeffSource === "loading" ? (
              <CalibrationRow>Loading...</CalibrationRow>
            ) : (
              <CalibrationRow>
                <CoeffSource>
                  {calCoeffSource === "calibration"
                    ? "from calibration"
                    : calCoeffSource === "johndeere"
                      // Deere's own mass (default coefficient 100): not an estimate.
                      ? <SourceBadge style={{ marginLeft: 0 }}>{jdLabel}</SourceBadge>
                      : `estimated${hasCalibrations ? "" : " (no calibrations)"}`}
                </CoeffSource>
                <Button
                  kind="primary"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (selStart) params.set("start", new Date(selStart).toISOString());
                    if (selEnd) params.set("stop", new Date(selEnd).toISOString());
                    const qs = params.toString();
                    navigate(
                      `${ROUTES.DEVICES_CALIBRATION.replace(":id", selectedDeviceId)}${qs ? `?${qs}` : ""}`
                    );
                  }}
                  style={{ width: "auto", minWidth: 0, padding: "4px 12px", fontSize: 12, height: 28 }}
                >
                  Calibrate
                </Button>
              </CalibrationRow>
            )}
          </Column>
        )}

      </ControlsRow>

      {/* Calendar with green dots + time range inputs */}
      {selectedDeviceId && (
        <ControlsRow style={{ alignItems: "flex-start" }}>
          {/* DatePicker calendar */}
          <div>
            <DatePicker
              selected={selectedDate}
              datesWithData={datesWithData}
              onMonthChange={(month) => setVisibleMonth(month)}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date);
                  // Set start to beginning of day, end to end of day (local time)
                  const dayStart = new Date(date);
                  dayStart.setHours(0, 0, 0, 0);
                  const dayEnd = new Date(date);
                  dayEnd.setHours(23, 59, 59, 0);
                  setStartTime(toLocalDatetimeString(dayStart));
                  setEndTime(toLocalDatetimeString(dayEnd));
                }
              }}
            />
          </div>

          {/* Time range inputs */}
          <Column style={{ gap: 12, paddingTop: 4 }}>
            <Column style={{ gap: 4 }}>
              <Label>{wt.startTime ?? "Start time"}</Label>
              <StyledInput
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </Column>
            <Column style={{ gap: 4 }}>
              <Label>{wt.endTime ?? "End time"}</Label>
              <StyledInput
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </Column>
          </Column>
        </ControlsRow>
      )}

      {rangeError && <ErrorText>{rangeError}</ErrorText>}

      {selectedDeviceId && jdNotice && (
        jdNotice.warn
          ? <NoticeText role="status">{jdNotice.text}</NoticeText>
          : <CoeffSource style={{ display: "block", marginBottom: 12 }}>{jdNotice.text}</CoeffSource>
      )}

      {/* Named plot markers */}
      {selectedDeviceId && !rangeError && (
        <Column style={{ gap: 12, marginBottom: 16 }}>
          <SubSectionTitle>
            {wt.markersSectionTitle ?? "Plot markers"}
          </SubSectionTitle>
          {chartData.length > 0 && (
            <Row style={{ gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <Button
                kind={placementMode === "point" ? "primary" : "outline"}
                onClick={() => {
                  setPlacementMode((p) => (p === "point" ? "none" : "point"));
                  setMarkerPending(null);
                  setMarkerDraftName("");
                }}
                disabled={loadingChart}
                style={{ width: "auto", minWidth: 0, padding: "6px 14px", fontSize: 13 }}
              >
                {placementMode === "point"
                  ? (wt.placePointMarkerActive ?? "Click chart to place…")
                  : (wt.placePointMarker ?? "Place point marker")}
              </Button>
              {placementMode === "point" && (
                <MarkerHint>
                  {wt.placePointMarkerHint ??
                    "Click on the chart at the time you want to annotate."}
                </MarkerHint>
              )}
            </Row>
          )}
          {markerPending && (
            <ResultBox style={{ marginBottom: 0 }}>
              <Column style={{ gap: 6, flex: 1, minWidth: 200 }}>
                <Label>
                  {markerPending.kind === "point"
                    ? (wt.markerNameLabel ?? "Name")
                    : (wt.markerIntervalNameLabel ?? "Name this interval")}
                </Label>
                <StyledInput
                  type="text"
                  value={markerDraftName}
                  onChange={(e) => setMarkerDraftName(e.target.value)}
                  placeholder={wt.markerNamePlaceholder ?? "e.g. New load"}
                  maxLength={200}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveMarker();
                  }}
                />
              </Column>
              <Row style={{ gap: 8, paddingTop: 18 }}>
                <Button
                  kind="primary"
                  onClick={handleSaveMarker}
                  disabled={savingMarker || !markerDraftName.trim()}
                  style={{ width: "auto", minWidth: 88 }}
                >
                  {savingMarker ? "…" : (wt.markerSave ?? "Save")}
                </Button>
                <Button
                  kind="outline"
                  onClick={() => {
                    setMarkerPending(null);
                    setMarkerDraftName("");
                  }}
                  style={{ width: "auto", minWidth: 88 }}
                >
                  {wt.markerCancel ?? "Cancel"}
                </Button>
              </Row>
            </ResultBox>
          )}
        </Column>
      )}

      {/* Chart */}
      {selectedDeviceId && !loadingChart && truncatedNotice && (
        <NoticeText role="status">{truncatedNotice}</NoticeText>
      )}
      {!selectedDeviceId ? (
        <EmptyState>{wt.noDeviceSelected ?? "Select a device to view weight data"}</EmptyState>
      ) : loadingChart ? (
        <EmptyState>Loading...</EmptyState>
      ) : chartData.length === 0 ? (
        <EmptyState>{wt.noData ?? "No data for the selected time range"}</EmptyState>
      ) : (
        <ChartContainer>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart
              data={drawnData}
              margin={{ top: 28, right: 10, left: 6, bottom: 6 }}
              {...chartMouse(drawnData)}
            >
              <defs>
                <linearGradient id="wt_fill" x1="0" y1="0" x2="0" y2="1">
                  {Array.from({ length: gradientLength }, (_, i) => {
                    const from = remap(i, 0, gradientLength, 0, 1);
                    const to = remap(i + 1, 0, gradientLength, 0, 1);
                    const value = normalizeSensor(
                      remap(gradientLength - i, 0, gradientLength, 0, Math.min(chartMax, 100) / 100)
                    );
                    if (i === gradientLength - 1) {
                      return (
                        <React.Fragment key={i}>
                          <stop offset={from} stopColor={sensorColor(value)} stopOpacity={1} />
                        </React.Fragment>
                      );
                    }
                    return (
                      <React.Fragment key={i}>
                        <stop offset={from} stopColor={sensorColor(value)} stopOpacity={1} />
                        <stop offset={to} stopColor={sensorColor(value)} stopOpacity={1} />
                      </React.Fragment>
                    );
                  })}
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="time"
                tickFormatter={(val) => moment.unix(val).format("HH:mm")}
                scale="linear"
                interval="equidistantPreserveStart"
              />
              <YAxis
                dataKey="sensor"
                tickFormatter={(val) => Math.round(val).toString()}
              />
              <Area
                type="monotone"
                dataKey="sensor"
                stroke="url(#wt_fill)"
                fill="url(#wt_fill)"
                fillOpacity={1}
                connectNulls={false}
                isAnimationActive={false}
                activeDot={{ r: 3 }}
              />
              <Tooltip
                labelFormatter={(label) => moment.unix(label).format("HH:mm:ss")}
                formatter={(value: number) => {
                  // A John Deere combine's value is its relative t/ha.
                  const name = selectedIsJohnDeere
                    ? (lang.tracks as Record<string, string | undefined>).yieldRelativeLayerTitle ??
                      "Yield (relative)"
                    : "Sensor";
                  if (value == null) return ["-", name];
                  return [Math.round(value), name];
                }}
              />
              {/* Drag selection reference area */}
              {dragSpan && (
                <ReferenceArea
                  x1={dragSpan.x1}
                  x2={dragSpan.x2}
                  strokeOpacity={0.3}
                  fill="#68ad18"
                  fillOpacity={0.15}
                />
              )}
              {/* Persisted selection from pickers */}
              {selection && refAreaLeft == null && (
                <ReferenceArea
                  x1={selection.x1}
                  x2={selection.x2}
                  strokeOpacity={0.3}
                  fill="#68ad18"
                  fillOpacity={0.15}
                />
              )}
              {intervalMarkers.map((m) => {
                const x1 = Math.floor(new Date(m.start).getTime() / 1000);
                const x2 = Math.floor(new Date(m.stop).getTime() / 1000);
                return (
                  <ReferenceArea
                    key={`mk-int-${m.id}`}
                    x1={x1}
                    x2={x2}
                    fill="#f59e0b"
                    fillOpacity={0.14}
                    stroke="#d97706"
                    strokeOpacity={0.35}
                    isFront
                  >
                    <RechartsLabel
                      position="insideTop"
                      content={(props) => (
                        <ChartMarkerNamePill
                          viewBox={props.viewBox as ChartLabelViewBox | undefined}
                          marker={m}
                          placement="interval"
                          onPillClick={handleMarkerPillClick}
                        />
                      )}
                    />
                  </ReferenceArea>
                );
              })}
              {pointMarkers.map((m) => {
                const x = Math.floor(new Date(m.start).getTime() / 1000);
                return (
                  <ReferenceLine
                    key={`mk-pt-${m.id}`}
                    x={x}
                    stroke="#68ad18"
                    strokeWidth={1.5}
                    strokeDasharray="5 4"
                    isFront
                  >
                    <RechartsLabel
                      position="top"
                      content={(props) => (
                        <ChartMarkerNamePill
                          viewBox={props.viewBox as ChartLabelViewBox | undefined}
                          marker={m}
                          placement="point"
                          onPillClick={handleMarkerPillClick}
                          pointStackIndex={pointMarkerStacks.get(m.id) ?? 0}
                        />
                      )}
                    />
                  </ReferenceLine>
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      )}

      {/* Potato-camera charts (same metrics as the Tracks panel) */}
      {!loadingChart && chartData.length > 0 &&
        cameraCharts.map(({ metric, rows }) => (
          <CameraChart
            key={metric.dataKey}
            rows={rows}
            dataKey={metric.dataKey}
            label={cameraLabel(metric.dataKey)}
            unit={metric.unit}
            decimals={metric.decimals}
            reversed={metric.reversed}
            drag={dragSpan}
            selection={refAreaLeft == null ? selectionSpan(rows, selStart, selEnd) : null}
            mouse={chartMouse(rows)}
          />
        ))}

      {/* Fuel sensor input (same metric as the Tracks "Fuel sensor" layer) */}
      {!loadingChart && fuelRows && fuelRows.length > 0 && (
        <CameraChart
          rows={fuelRows}
          dataKey="fuel"
          label={tracksLang.fuelLayerTitle ?? "Fuel sensor"}
          unit="mV"
          decimals={0}
          reversed={false}
          pallet={FUEL_PALLET}
          pad={10}
          drag={dragSpan}
          selection={refAreaLeft == null ? selectionSpan(fuelRows, selStart, selEnd) : null}
          mouse={chartMouse(fuelRows)}
        />
      )}

      {/* Selection time pickers + calculate */}
      {chartData.length > 0 && (
        <ControlsRow>
          <Column style={{ gap: 4 }}>
            <Label>{wt.selectionStart ?? "Selection start"}</Label>
            <StyledInput
              type="datetime-local"
              value={selStart}
              onChange={(e) => {
                setSelStart(e.target.value);
                setCalculatedWeightKg(null);
              }}
            />
          </Column>
          <Column style={{ gap: 4 }}>
            <Label>{wt.selectionEnd ?? "Selection end"}</Label>
            <StyledInput
              type="datetime-local"
              value={selEnd}
              onChange={(e) => {
                setSelEnd(e.target.value);
                setCalculatedWeightKg(null);
              }}
            />
          </Column>
          <div style={{ paddingTop: 20, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <Button
              kind="outline"
              onClick={() => {
                if (!selStart || !selEnd) return;
                setMarkerPending({ kind: "interval", start: selStart, end: selEnd });
                setMarkerDraftName("");
              }}
              disabled={!selStart || !selEnd}
              style={{ width: "auto", minWidth: 160 }}
            >
              {wt.addIntervalMarker ?? "Add interval marker"}
            </Button>
            <Button
              kind="primary"
              onClick={handleCalculate}
              disabled={!selStart || !selEnd || calculating}
              style={{ width: "auto", minWidth: 160 }}
            >
              {calculating ? "..." : (wt.calculateWeight ?? "Calculate Weight")}
            </Button>
            {calculating && (
              <Button
                kind="outline"
                onClick={handleCancelCalculate}
                style={{ width: "auto", minWidth: 100 }}
              >
                {wt.cancel ?? "Cancel"}
              </Button>
            )}
          </div>
          {calculating && calcProgress && calcProgress.total > 0 && (
            <CalcProgress
              done={calcProgress.done}
              total={calcProgress.total}
              runningKg={calcProgress.runningKg}
              toDisplayUnit={toDisplayUnit}
              unitLabel={unitLabel}
              labelTemplate={wt.calculatingProgress}
              runningLabel={wt.runningTotal ?? "Running total"}
            />
          )}
        </ControlsRow>
      )}

      {/* Result + Save */}
      {calculatedWeightKg != null && (
        <ResultBox>
          <div>
            <Label>{wt.totalWeight ?? "Total Weight"}</Label>
            <div>
              <ResultValue>
                {toDisplayUnit(calculatedWeightKg).toFixed(2)} {unitLabel}
              </ResultValue>
            </div>
          </div>
          {calculatedCamera && (
            <>
              <div>
                <Label>{cameraLabel("potato")}</Label>
                <div><ResultValue>{formatCount(calculatedCamera.potatoCount)}</ResultValue></div>
              </div>
              <div>
                <Label>{cameraLabel("stone")}</Label>
                <div><ResultValue>{formatCount(calculatedCamera.stoneCount)}</ResultValue></div>
              </div>
              <div>
                <Label>{cameraLabel("size")}</Label>
                <SizeWithHist>
                  <ResultValue>{formatSizePx(calculatedCamera.avgSizePx)}</ResultValue>
                  {hasSizes(calculatedCamera.sizeHist) && (
                    <SizeHistogram hist={calculatedCamera.sizeHist} compact />
                  )}
                </SizeWithHist>
              </div>
            </>
          )}
          <Button
            kind="primary"
            onClick={handleSave}
            style={{ width: "auto", minWidth: 100 }}
          >
            {wt.saveRecord ?? "Save"}
          </Button>
        </ResultBox>
      )}

      {/* Saved records table */}
      {deviceEsn && (
        <>
          {records.length > 0 && (
            <>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <Th>{wt.startTime ?? "Start"}</Th>
                      <Th>{wt.endTime ?? "End"}</Th>
                      <Th>{wt.totalWeight ?? "Weight"} ({unitLabel})</Th>
                      {recordsHaveCamera && (
                        <>
                          <Th>{cameraLabel("potato")}</Th>
                          <Th>{cameraLabel("stone")}</Th>
                          <Th>{cameraLabel("size")} (px)</Th>
                        </>
                      )}
                      <Th>{wt.recordedBy ?? "Recorded by"}</Th>
                      <Th>{wt.comment ?? "Comment"}</Th>
                      <Th></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((rec) => (
                      <RecordRow
                        key={rec.id}
                        record={rec}
                        unitLabel={unitLabel}
                        toDisplayUnit={toDisplayUnit}
                        showCamera={recordsHaveCamera}
                        onUpdateComment={handleUpdateComment}
                        onDelete={handleDelete}
                        deleteLabel={wt.deleteRecord ?? "Delete"}
                        mobileLabel={wt.sourceMobile ?? "Mobile"}
                        mobileHint={wt.sourceMobileHint ?? "Recorded in the driver app"}
                      />
                    ))}
                  </tbody>
                </Table>
              </TableScroll>

              {/* CSV export */}
              <Row style={{ marginTop: 12, gap: 8 }}>
                <Button
                  kind="outline"
                  onClick={() => {
                    window.open(weightRecordsCsvUrl(deviceEsn), "_blank");
                  }}
                  style={{ width: "auto", minWidth: 140 }}
                >
                  {wt.exportCsv ?? "Export CSV"}
                </Button>
              </Row>
            </>
          )}

          {!loadingRecords && records.length === 0 && (
            <EmptyState style={{ padding: 16 }}>
              {wt.noRecords ?? "No saved records yet"}
            </EmptyState>
          )}
        </>
      )}

      {markerMenu &&
        createPortal(
          <MarkerPopoverPanel
            id="marker-chart-popover"
            style={{ top: markerMenu.anchor.top, left: markerMenu.anchor.left }}
          >
            {markerMenu.mode === "actions" && (
              <MarkerPopoverActions>
                <MarkerPopoverBtn
                  type="button"
                  onClick={() => {
                    const m = markers.find((u) => u.id === markerMenu.markerId);
                    setMarkerEditName(m?.label ?? "");
                    setMarkerMenu((prev) =>
                      prev ? { ...prev, mode: "edit" } : prev
                    );
                  }}
                >
                  {wt.markerEdit ?? "Edit"}
                </MarkerPopoverBtn>
                <MarkerPopoverBtn
                  type="button"
                  danger
                  onClick={async () => {
                    const ok = await handleDeleteMarker(markerMenu.markerId);
                    if (ok) closeMarkerMenu();
                  }}
                >
                  {wt.markerDelete ?? "Delete"}
                </MarkerPopoverBtn>
              </MarkerPopoverActions>
            )}
            {markerMenu.mode === "edit" && (
              <Column style={{ gap: 8, minWidth: 200 }}>
                <Label>{wt.markerNameLabel ?? "Name"}</Label>
                <StyledInput
                  type="text"
                  value={markerEditName}
                  onChange={(e) => setMarkerEditName(e.target.value)}
                  maxLength={200}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveMarkerLabelFromPopover();
                  }}
                />
                <Row style={{ gap: 8, marginTop: 4 }}>
                  <Button
                    kind="primary"
                    onClick={saveMarkerLabelFromPopover}
                    disabled={!markerEditName.trim()}
                    style={{ width: "auto", minWidth: 72 }}
                  >
                    {wt.markerSave ?? "Save"}
                  </Button>
                  <Button
                    kind="outline"
                    onClick={closeMarkerMenu}
                    style={{ width: "auto", minWidth: 72 }}
                  >
                    {wt.markerCancel ?? "Cancel"}
                  </Button>
                </Row>
              </Column>
            )}
          </MarkerPopoverPanel>,
          document.body
        )}
    </SectionCard>
  );
});

// ────────────────────────────────────────────────────────────
// RecordRow (editable comment)
// ────────────────────────────────────────────────────────────

const RecordRow: React.FC<{
  record: WeightRecord;
  unitLabel: string;
  toDisplayUnit: (kg: number) => number;
  /** The table shows the potato-camera columns. */
  showCamera: boolean;
  onUpdateComment: (id: string, comment: string) => void;
  onDelete: (id: string) => void;
  deleteLabel: string;
  mobileLabel: string;
  mobileHint: string;
}> = React.memo(({
  record,
  unitLabel,
  toDisplayUnit,
  showCamera,
  onUpdateComment,
  onDelete,
  deleteLabel,
  mobileLabel,
  mobileHint,
}) => {
  const [comment, setComment] = useState(record.comment ?? "");
  const [dirty, setDirty] = useState(false);

  const handleBlur = useCallback(() => {
    if (dirty) {
      onUpdateComment(record.id, comment);
      setDirty(false);
    }
  }, [dirty, comment, record.id, onUpdateComment]);

  // Loads saved before the histogram was kept have none. Desktop draws the
  // preview inside the size cell; a phone, where the row is already wider
  // than the screen, keeps the full histogram on its own line under the load.
  const isMobile = useIsMobile();
  const hist = showCamera && hasSizes(record.cvSizeHist) ? record.cvSizeHist : null;
  const histLine = hist && isMobile ? hist : null;

  return (
    <>
      <tr className={histLine ? "with-hist" : undefined}>
        <Td>{moment(record.start).format("YYYY-MM-DD HH:mm")}</Td>
        <Td>{moment(record.stop).format("YYYY-MM-DD HH:mm")}</Td>
        <Td>{toDisplayUnit(record.weightKg).toFixed(2)}</Td>
        {showCamera && (
          <>
            <Td>{formatCount(record.cvPotatoCount)}</Td>
            <Td>{formatCount(record.cvStoneCount)}</Td>
            <Td>
              <SizeWithHist>
                {record.cvAvgSizePx == null ? "—" : record.cvAvgSizePx.toFixed(1)}
                {hist && !isMobile && <SizeHistogram hist={hist} compact />}
              </SizeWithHist>
            </Td>
          </>
        )}
        <Td title={record.recordedBy?.email ?? undefined}>
          {recordedByLabel(record)}
          {record.source === "mobile" && (
            <SourceBadge title={mobileHint}>{mobileLabel}</SourceBadge>
          )}
        </Td>
        <Td>
          <CommentInput
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              setDirty(true);
            }}
            onBlur={handleBlur}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleBlur();
            }}
            placeholder="..."
          />
        </Td>
        <Td>
          <DeleteButton onClick={() => onDelete(record.id)}>{deleteLabel}</DeleteButton>
        </Td>
      </tr>
      {histLine && (
        <tr>
          {/* Every column of the camera table: 3 + 3 camera + 3. */}
          <Td colSpan={9}>
            <SizeHistogram hist={histLine} />
          </Td>
        </tr>
      )}
    </>
  );
});

export default WeightTracking;
